import React, { useEffect, useState } from 'react';
import { useNotification } from '@ohif/ui-next';
import { useNavigate } from 'react-router-dom';

import neuroplanLogo from '../../assets/neuroplan-logo/horizontal-dark-logo.png';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const LOGO_SOURCES = [
  neuroplanLogo,
];

const Login = () => {
  const navigate = useNavigate();
  const { show } = useNotification();
  const { isAuthenticated, isLoading, login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [logoIndex, setLogoIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(user?.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, user]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const authenticatedUser = await login(email, password);
      navigate(authenticatedUser.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
    } catch {
      show({
        title: 'Authentication Error',
        message: 'Invalid email or password.',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoError = () => {
    setLogoIndex((currentIndex) => {
      const nextIndex = currentIndex + 1;
      return nextIndex < LOGO_SOURCES.length ? nextIndex : currentIndex;
    });
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="login-container">
      <div className="login-pattern" aria-hidden="true" />
      <div className="login-glow login-glow-left" aria-hidden="true" />
      <div className="login-glow login-glow-right" aria-hidden="true" />

      <div className="login-card">
        <div className="login-header">
          <div className="login-brand" aria-label="NeuroPlan">
            <div className="login-logo-frame">
              <img
                src={LOGO_SOURCES[logoIndex]}
                onError={handleLogoError}
                alt="NeuroPlan"
                className="login-logo-image"
                draggable={false}
              />
            </div>
          </div>

          <h2 className="login-title">Sign in</h2>
          <p className="login-subtitle">Sign in to access your NeuroPlan medical imaging dashboard</p>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label className="form-label" htmlFor="email">
              Email Address
            </label>
            <div className="form-input-container">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                placeholder="doctor@hospital.com"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">
              Password
            </label>
            <div className="form-input-container">
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="form-options">
            <div className="remember-me-container">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="remember-me-checkbox"
              />
              <label htmlFor="remember-me" className="remember-me-label">
                Remember me
              </label>
            </div>

            <a href="#" className="forgot-password">
              Forgot password?
            </a>
          </div>

          <button type="submit" className="submit-button">
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
